/**
 * Backlinks panel: pages that link to the current one through wiki-links.
 * Derived 100 % locally (LIKE over block.content in SQLite) — no extra table
 * or round-trip to the server.
 */
import { For, Show } from "solid-js";
import { Link } from "@tanstack/solid-router";
import { Link2 } from "@fleet/icons";
import { msg } from "@lingui/core/macro";
import { useBacklinksQuery } from "@apunte-digital/shared/solid";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useI18n } from "@/providers/i18n-provider";

interface PageBacklinksProps {
  pageId: string;
}

export function PageBacklinks(props: PageBacklinksProps) {
  const { t } = useI18n();
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();

  const backlinks = useBacklinksQuery({
    db: () => db(),
    drizzle,
    pageId: () => props.pageId,
  });

  // Exclude self-references (a page that links to itself)
  const rows = () => (backlinks.data ?? []).filter((row) => row.id !== props.pageId);

  return (
    <Show when={rows().length > 0}>
      <section class="mt-6">
        <h3 class="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground/70">
          <Link2 class="h-3.5 w-3.5" />
          {t(msg`Linked from`)}
          <span class="opacity-60">({rows().length})</span>
        </h3>
        <ul class="mt-2 space-y-0.5">
          <For each={rows()}>
            {(row) => (
              <li>
                <Link
                  to="/notes/$pageId"
                  params={{ pageId: row.id }}
                  class="flex items-center gap-1.5 rounded-md px-2 py-1 text-sm text-muted-foreground/80 hover:bg-accent/5 hover:text-foreground"
                >
                  <span class="shrink-0 text-base leading-none">{row.icon ?? "📄"}</span>
                  <span class="truncate">
                    {row.title?.trim() && row.title.trim().length > 0
                      ? row.title
                      : t(msg`Untitled`)}
                  </span>
                </Link>
              </li>
            )}
          </For>
        </ul>
      </section>
    </Show>
  );
}
